/**
 * Extracted Fields Panel
 * =======================
 * Displays structured entities extracted from documents.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  FileSpreadsheet, Hash, Calendar, DollarSign, Building2, 
  User, MapPin, ChevronDown, ChevronUp, Copy, Check,
  AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/clipboard';

const ExtractedFieldsPanel = ({ aiInsight, documentType }) => {
  const [expandedField, setExpandedField] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  if (!aiInsight || !aiInsight.entities) {
    return (
      <Card data-testid="extracted-fields-panel">
        <CardContent className="py-8 text-center text-slate-500">
          <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>No extracted data available</p>
          <p className="text-sm">Process the document to extract structured data</p>
        </CardContent>
      </Card>
    );
  }

  const entities = aiInsight.entities.entities || {};
  const classification = aiInsight.classification || {};
  const unmatched = aiInsight.entities.unmatched_important || [];

  const copyField = (key, value) => {
    const textValue = typeof value === 'object' 
      ? JSON.stringify(value.value || value, null, 2) 
      : String(value);
    copyToClipboard(textValue).then(ok => {
      if (ok) {
        setCopiedField(key);
        toast.success(`Copied ${key}`);
        setTimeout(() => setCopiedField(null), 2000);
      } else {
        toast.error('Copy failed');
      }
    });
  };

  const getFieldIcon = (key) => {
    const keyLower = key.toLowerCase();
    if (keyLower.includes('date') || keyLower.includes('period')) return <Calendar className="w-4 h-4" />;
    if (keyLower.includes('amount') || keyLower.includes('total') || keyLower.includes('price')) return <DollarSign className="w-4 h-4" />;
    if (keyLower.includes('number') || keyLower.includes('id') || keyLower.includes('gstin') || keyLower.includes('pan')) return <Hash className="w-4 h-4" />;
    if (keyLower.includes('vendor') || keyLower.includes('company') || keyLower.includes('organization')) return <Building2 className="w-4 h-4" />;
    if (keyLower.includes('name') || keyLower.includes('buyer') || keyLower.includes('customer')) return <User className="w-4 h-4" />;
    if (keyLower.includes('address') || keyLower.includes('location')) return <MapPin className="w-4 h-4" />;
    return <FileSpreadsheet className="w-4 h-4" />;
  };

  const getConfidenceColor = (conf) => {
    if (!conf) return 'bg-slate-100 text-slate-600';
    if (conf >= 0.8) return 'bg-green-100 text-green-700';
    if (conf >= 0.5) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    
    if (typeof value === 'object') {
      if (value.value !== undefined) {
        // Handle nested value objects
        if (typeof value.value === 'object') {
          // Currency or complex object
          if (value.value.amount !== undefined) {
            return `${value.value.currency || '₹'} ${value.value.amount.toLocaleString()}`;
          }
          return JSON.stringify(value.value);
        }
        return String(value.value);
      }
      if (value.amount !== undefined) {
        return `${value.currency || '₹'} ${value.amount.toLocaleString()}`;
      }
      return JSON.stringify(value);
    }
    
    return String(value);
  };

  // Group entities by category
  const groupedEntities = {
    identifiers: [],
    financial: [],
    parties: [],
    dates: [],
    other: []
  };

  Object.entries(entities).forEach(([key, value]) => {
    const keyLower = key.toLowerCase();
    const entity = { key, value };
    
    if (keyLower.includes('number') || keyLower.includes('id') || keyLower.includes('gstin') || keyLower.includes('pan') || keyLower.includes('ref')) {
      groupedEntities.identifiers.push(entity);
    } else if (keyLower.includes('amount') || keyLower.includes('total') || keyLower.includes('price') || keyLower.includes('tax') || keyLower.includes('subtotal')) {
      groupedEntities.financial.push(entity);
    } else if (keyLower.includes('vendor') || keyLower.includes('buyer') || keyLower.includes('name') || keyLower.includes('party') || keyLower.includes('customer')) {
      groupedEntities.parties.push(entity);
    } else if (keyLower.includes('date') || keyLower.includes('period') || keyLower.includes('year')) {
      groupedEntities.dates.push(entity);
    } else {
      groupedEntities.other.push(entity);
    }
  });

  const renderEntityGroup = (entities, title, icon) => {
    if (entities.length === 0) return null;
    
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-slate-600 flex items-center gap-2">
          {icon}
          {title}
        </h4>
        <div className="space-y-2">
          {entities.map(({ key, value }) => {
            const conf = typeof value === 'object' ? value.confidence : null;
            const isExpanded = expandedField === key;
            
            return (
              <div 
                key={key}
                className="p-3 bg-white border rounded-lg hover:border-blue-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2 flex-1">
                    <span className="text-slate-400 mt-0.5">
                      {getFieldIcon(key)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-700">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        {conf && (
                          <Badge className={`text-xs ${getConfidenceColor(conf)}`}>
                            {Math.round(conf * 100)}%
                          </Badge>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-slate-900">
                        {formatValue(value)}
                      </div>
                      {typeof value === 'object' && value.source_text && isExpanded && (
                        <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-500">
                          <span className="font-medium">Source:</span> "{value.source_text}"
                        </div>
                      )}
                      {typeof value === 'object' && value.original_value && (
                        <div className="mt-1 text-xs text-slate-500">
                          Original: {value.original_value}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => copyField(key, value)}
                    >
                      {copiedField === key ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    {typeof value === 'object' && value.source_text && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedField(isExpanded ? null : key)}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Card data-testid="extracted-fields-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              Extracted Data
            </CardTitle>
            <CardDescription>
              {Object.keys(entities).length} fields extracted from document
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {classification.category && (
              <Badge className="bg-blue-100 text-blue-700">
                {classification.category}
              </Badge>
            )}
            {classification.confidence && (
              <span className="text-xs text-slate-500">
                {Math.round(classification.confidence * 100)}% match
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="grouped" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="grouped">By Category</TabsTrigger>
            <TabsTrigger value="all">All Fields</TabsTrigger>
          </TabsList>
          
          <TabsContent value="grouped" className="space-y-6">
            {renderEntityGroup(groupedEntities.identifiers, 'Identifiers & References', <Hash className="w-4 h-4" />)}
            {renderEntityGroup(groupedEntities.financial, 'Financial', <DollarSign className="w-4 h-4" />)}
            {renderEntityGroup(groupedEntities.parties, 'Parties & Names', <User className="w-4 h-4" />)}
            {renderEntityGroup(groupedEntities.dates, 'Dates & Periods', <Calendar className="w-4 h-4" />)}
            {renderEntityGroup(groupedEntities.other, 'Other Fields', <FileSpreadsheet className="w-4 h-4" />)}
          </TabsContent>
          
          <TabsContent value="all" className="space-y-2">
            {Object.entries(entities).map(([key, value]) => {
              const conf = typeof value === 'object' ? value.confidence : null;
              
              return (
                <div 
                  key={key}
                  className="flex items-center justify-between p-2 bg-slate-50 rounded"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">{getFieldIcon(key)}</span>
                    <span className="text-sm font-medium">{key}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{formatValue(value)}</span>
                    {conf && (
                      <Badge className={`text-xs ${getConfidenceColor(conf)}`}>
                        {Math.round(conf * 100)}%
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </TabsContent>
        </Tabs>

        {/* Unmatched Important Items */}
        {unmatched.length > 0 && (
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <h4 className="text-sm font-medium text-yellow-800 flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" />
              Additional Information Found
            </h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              {unmatched.map((item, idx) => (
                <li key={idx}>• {item}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Classification Notes */}
        {classification.reasoning && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="text-sm font-medium text-blue-800 flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4" />
              Classification Reasoning
            </h4>
            <p className="text-sm text-blue-700">{classification.reasoning}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExtractedFieldsPanel;
